// Access lists for the dashboard settings drawer (OTC-3138). Viewers and Editors each hold roles and
// users together, keyed so default roles, custom roles and users never collide:
// 'r:<company_user_roles.id>', 'c:<company_roles.id>', 'u:<company_users.id>'.

export interface RoleRef { role_id: number | null; company_role_id: number | null }
export interface RoleOption extends RoleRef { name: string }
export interface AllowedRole extends RoleRef { can_read: boolean; can_write: boolean }

export function roleKey(r: RoleRef): string {
  return r.role_id != null ? 'r:' + r.role_id : 'c:' + r.company_role_id
}

export function userKey(id: number): string {
  return 'u:' + id
}

export function userIdsFromKeys(keys: string[]): number[] {
  return keys.filter((k) => k.startsWith('u:')).map((k) => Number(k.slice(2)))
}

function parseRoleKey(key: string): RoleRef | null {
  if (key.startsWith('r:')) return { role_id: Number(key.slice(2)), company_role_id: null }
  if (key.startsWith('c:')) return { role_id: null, company_role_id: Number(key.slice(2)) }
  return null
}

// A role in both lists gets both flags. Sorted by key so two builds of the same selection compare equal.
export function mergeRolePermissions(viewerKeys: string[], editorKeys: string[]): AllowedRole[] {
  const byKey = new Map<string, AllowedRole>()
  const add = (key: string, flag: 'can_read' | 'can_write') => {
    const ref = parseRoleKey(key)
    if (!ref) return
    const row = byKey.get(key) || { ...ref, can_read: false, can_write: false }
    row[flag] = true
    byKey.set(key, row)
  }
  for (const k of viewerKeys) add(k, 'can_read')
  for (const k of editorKeys) add(k, 'can_write')
  return Array.from(byKey.keys()).sort().map((k) => byKey.get(k) as AllowedRole)
}

// The same sorted list from the rows view-dashboard-by-id returns (can_read / can_write arrive as 0 or 1).
export function allowedRolesFromRows(rows: Array<RoleRef & { can_read: unknown; can_write: unknown }>): AllowedRole[] {
  const viewers = rows.filter((r) => Number(r.can_read) === 1).map(roleKey)
  const editors = rows.filter((r) => Number(r.can_write) === 1).map(roleKey)
  return mergeRolePermissions(viewers, editors)
}
